import axios from 'axios'
import { toast } from 'react-toastify';
import { camelizeKeys, decamelizeKeys } from 'humps'
import Portico from '@ordernext/networking-stadium/portico'
import Stadium from '@ordernext/networking-stadium/stadium'
import { get, uniq } from 'lodash'
import createDataDogLog from '../utils/datadog';
const generateUuid = require('uuid/v4')
let fetchingTokens = false

const instance = axios.create({
  transformResponse: [...axios.defaults.transformResponse, camelizeKeys]
})

instance.interceptors.request.use((config) => {

  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    'Cache-Control': 'no-cache, no-store',
    Pragma: 'no-cache',
    Expires: 0,
  }

  config.baseURL = process.env.REACT_APP_BASE_URL
  config.params = decamelizeKeys(config?.params ?? {})
  config.data = decamelizeKeys(config?.data ?? {})

  return config
}, (error) => Promise.reject(error))

instance.interceptors.response.use((res) => {
  return res
}, async (err) => {
  const response = err?.response
  const config = err?.config

  if (response?.status === 401 && response?.data?.error === 'invalid_token' && !config?.sent && !fetchingTokens) {
    fetchingTokens = true
    config.sent = true

    const deviceId = window.localStorage.device_id
    const appKey = process.env.REACT_APP_OIDC_CLIENT_ID
    const organizationName = process.env.REACT_APP_ORG

    try {
      const response = await Portico.deviceLogin(deviceId, appKey, organizationName)

      const accessToken = get(response, 'data.accessToken', undefined)
      const expiresAt = get(response, 'data.expiresAt', undefined)

      if (accessToken && expiresAt) {
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('expiresAt', expiresAt)

        Stadium.config = {
          ...Stadium.config,
          headers: {
            ...Stadium.config.headers,
            Authorization: `Bearer ${accessToken}`,
            'Cache-Control': 'no-cache, no-store',
            Pragma: 'no-cache',
            Expires: 0,
          }
        }
      }

      fetchingTokens = false
      config.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`
      return axios(config)
    } catch (error) {
      fetchingTokens = false
      return Promise.reject(error)
    }
  }

  const applicationMessage = err?.response?.data?.errorMessage;
  const friendlyMessage = err?.response?.data?.friendlyMessage;
  const errorMessage = friendlyMessage ?? applicationMessage;
  if (errorMessage && err?.message !== "Network Error") {
    // TODO temporary, will refactor this soon to a more refine system of what errors from stadium do we want to toast
    if (!errorMessage.includes('discount_charge_qr')) {
      toast.error(`${errorMessage}`, { autoClose: false })
    }
    createDataDogLog('error', errorMessage)
  }
  return Promise.reject(err)
})

class Remote {
  static deviceRoute = isKiosk => isKiosk ? '/kiosk/v3/orders' : '/pos/orders'

  static createPosOrder(params = {}, isKiosk) {
    return instance.post(`${Remote.deviceRoute(isKiosk)}`, params)
  }

  static getOrderDetails = (orderUuid) => instance.get(`/pos/orders/${orderUuid}`)

  static updatePosOrder(uuid, params = {}, isKiosk) {
    return instance.put(`${Remote.deviceRoute(isKiosk)}/${uuid}`, params)
  }

  static fetchMenus = ({ organizationName, standUuids, paymentType }) => {
    let translatedPaymentType = paymentType ? (paymentType === 'fcc' ? 'freedompay' : 'shift4') : null
    var payload = {
      params: { organizationName, venueUuid: process.env.REACT_APP_VENUE_UUID, paymentType: translatedPaymentType },
    }

    if (standUuids !== undefined && standUuids !== null) {
      payload = {
        params: { organizationName, uuids: standUuids, paymentType: translatedPaymentType },
      }
    }

    return instance.get('/pos/stands', payload)
  }

  static addLineItems = (orderUuid, orderMenuItems, isKiosk) => {
    const payload = { orderUuid, orderMenuItems };
    return instance.post(`${Remote.deviceRoute(isKiosk)}/${orderUuid}/line_items`, payload)
  }

  static closeTabOrder = (orderId, receiptSignature, tipAmountInCents, payments, isKiosk) => {
   const payload = {
      uuid: orderId,
      receiptSignature,
      tipAmountInCents,
      payments,
    }

    return instance.post(`${Remote.deviceRoute(isKiosk)}/${orderId}/close`, payload)
  }

  static refundOrderItems = (orderUuid, reason, lineItemUuids) => {
    const payload = {
      orderUuid,
      reason,
      userDetails: {},
      lineItemUuids,
    }

    return instance.post('/pos/refund', payload)
  }

  static voidItems = (orderUuid, reason, lineItemUuids) => {
    const payload = {
      data: {
        orderUuid,
        reason,
        userDetails: {},
        lineItemUuids,
      }
    }

    return instance.delete(`/pos/orders/${orderUuid}/line_items`, payload)
  }

  static getEmployeeRoles = () => {
    return instance.get('/operator/employee_roles')
  }

  static getAttendants = (organizationName) => {
    const payload = {
      params: { organizationName }
    }

    return instance.get('/operator/attendants', payload)
  }

  static getAttendantSettings = (pinNumber, venueUuid) => {
    const payload = {
      params: { pinNumber, venueUuid },
    }

    return instance.get('/api/v1/attendant_settings', payload)
  }

  static getPromotions = (venueUuid, standMenuUuids) => {
    const payload = {
      params: { venueUuid, standMenuUuids },
    }

    return instance.get('/pos/promotions', payload)
  }

  static getRemoteOrderTotal = (orderMenuItems, standMenuUuid, promotions, configOverride = {}, isKiosk, affiliations = []) => {
    let payload = {
      affiliations,
      promotions,
      orderMenuItems,
      standMenuUuid,
      taxExempt: false
    }
    payload.uuid = generateUuid()
    return instance.post(`${Remote.deviceRoute(isKiosk)}/total`, payload, configOverride)
  }

  static getSummaryReport = (deviceId) => {
    const payload = {
      params: { deviceId },
    }

    return instance.get('/pos/order_reports', payload)
  }

  static sendEmailJobToStadium = ({ orderUuid, emails, paymentUuid }) => {
    const payload = decamelizeKeys({
      emails,
      paymentUuid,
   })
    return instance.post(`/pos/orders/${orderUuid}/emails/PosOrderReceiptEmailJob`, payload)
  }

  static getTicketBalance = (ticketId) => {

    return instance.get(`/pos/tickets/${ticketId}`)
  }

  static getQRBalance = (qrCode) => {

    return instance.get(`/pos/nonces/${qrCode}`)
  }

  static getGiftCardBalance = (giftCardId) => {

    return instance.get(`/pos/gift_cards/${giftCardId}`)
  }

  // config requests
  static getConfig = () => instance.get(`/api/v1/app_device_configurations`)

  static createConfig = (params = {}) => {
    const payload = decamelizeKeys({
      ...params,
    })

    return instance.post('/api/v1/app_device_configurations', payload)
  }

  static updateConfig = (params = {}) => {
    const payload = decamelizeKeys({
      ...params,
    })

    return instance.put(`/api/v1/app_device_configurations?uuid=${params.uuid}`, payload)
  }
}

export default Remote
